import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { X, CheckCheck, MessageCircle, CheckSquare, Briefcase, AlertTriangle, Zap, Megaphone, Clock, Bell, Trash2, ExternalLink, ClipboardList } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  whatsapp:          { icon: MessageCircle, color: "#10b981", label: "WhatsApp" },
  form_lead:         { icon: ClipboardList, color: "#6366f1", label: "Formulário" },
  tarefa_atribuida:  { icon: CheckSquare,   color: "#3b82f6", label: "Tarefa" },
  tarefa_vencendo:   { icon: Clock,         color: "#f59e0b", label: "Tarefa" },
  negocio_atribuido: { icon: Briefcase,     color: "#6366f1", label: "Negócio" },
  sla_breach:        { icon: AlertTriangle, color: "#ef4444", label: "SLA" },
  sistema:           { icon: Zap,           color: "#94a3b8", label: "Sistema" },
  broadcast:         { icon: Megaphone,     color: "#f97316", label: "Aviso" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `há ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `há ${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function groupByDate(items) {
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();
  const groups = {};
  items.forEach(n => {
    const d = new Date(n.created_at).toDateString();
    const label = d === today ? "Hoje" : d === yesterday ? "Ontem" : new Date(n.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "long" });
    if (!groups[label]) groups[label] = [];
    groups[label].push(n);
  });
  return Object.entries(groups);
}

export default function NotificationDrawer({ open, onClose, onCountChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3b82f6";
  const navigate = useNavigate();
  const drawerRef = useRef(null);

  const fetchNotifs = useCallback(async (p = 1, append = false) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications?page=${p}&limit=30`, { withCredentials: true });
      const data = res.data;
      setItems(prev => append ? [...prev, ...data.items] : data.items);
      setHasMore(p < data.pages);
      setPage(p);
      if (onCountChange) onCountChange(data.items.filter(n => !n.lida).length);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [onCountChange]);

  useEffect(() => {
    if (open) fetchNotifs(1);
  }, [open, fetchNotifs]);

  // React to SSE notification events
  useEffect(() => {
    const onSSE = () => { if (open) fetchNotifs(1); };
    window.addEventListener("pipe4:notification", onSSE);
    return () => window.removeEventListener("pipe4:notification", onSSE);
  }, [open, fetchNotifs]);

  // Close on outside click
  useEffect(() => {
    const handle = (e) => {
      if (open && drawerRef.current && !drawerRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open, onClose]);

  const markRead = async (id) => {
    await axios.patch(`${API}/notifications/${id}/read`, {}, { withCredentials: true });
    setItems(prev => prev.map(n => n.id === id ? { ...n, lida: true } : n));
    if (onCountChange) onCountChange(items.filter(n => !n.lida && n.id !== id).length);
  };

  const deleteNotif = async (e, id) => {
    e.stopPropagation();
    await axios.delete(`${API}/notifications/${id}`, { withCredentials: true });
    setItems(prev => prev.filter(n => n.id !== id));
  };

  const markAllRead = async () => {
    await axios.post(`${API}/notifications/read-all`, {}, { withCredentials: true });
    setItems(prev => prev.map(n => ({ ...n, lida: true })));
    if (onCountChange) onCountChange(0);
  };

  const handleClick = async (notif) => {
    if (!notif.lida) await markRead(notif.id);
    if (notif.type === "whatsapp" && notif.negocio_id) {
      // Navigate to Kanban and open the deal modal directly on the WhatsApp tab
      navigate(`/negocios?open=${notif.negocio_id}&tab=whatsapp`);
    } else if (notif.link) {
      navigate(notif.link);
    }
    onClose();
  };

  const unreadCount = items.filter(n => !n.lida).length;
  const groups = groupByDate(items);

  return (
    <>
      {/* Backdrop */}
      {open && <div className="fixed inset-0 z-40 bg-black/10" onClick={onClose} />}

      {/* Drawer */}
      <div ref={drawerRef}
        className={`fixed top-0 right-0 h-full w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <Bell className="w-4 h-4 text-slate-600" />
            <h2 className="text-sm font-semibold text-slate-800">Notificações</h2>
            {unreadCount > 0 && (
              <span className="text-xs font-bold text-white px-1.5 py-0.5 rounded-full" style={{ backgroundColor: tc500 }}>
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <button onClick={markAllRead} title="Marcar tudo como lido"
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded hover:bg-slate-100 transition-colors">
                <CheckCheck className="w-3.5 h-3.5" />Ler todas
              </button>
            )}
            <button onClick={() => { navigate("/notificacoes"); onClose(); }}
              title="Ver todas" className="text-slate-400 hover:text-slate-600 p-1.5 rounded hover:bg-slate-100 transition-colors">
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1.5 rounded hover:bg-slate-100 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading && items.length === 0 && (
            <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
              <div className="animate-spin w-5 h-5 border-2 rounded-full mr-2" style={{ borderColor: `${tc500}40`, borderTopColor: tc500 }} />
              Carregando...
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Bell className="w-10 h-10 mb-3 text-slate-200" />
              <p className="text-sm font-medium text-slate-500">Nenhuma notificação</p>
              <p className="text-xs mt-1">Você está em dia!</p>
            </div>
          )}

          {groups.map(([label, notifs]) => (
            <div key={label}>
              <p className="px-4 py-2 text-[11px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50 border-b border-slate-100">
                {label}
              </p>
              {notifs.map(notif => {
                const cfg = TYPE_CONFIG[notif.type] || TYPE_CONFIG.sistema;
                const Icon = cfg.icon;
                return (
                  <div key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={`flex items-start gap-3 px-4 py-3.5 border-b border-slate-100 cursor-pointer group transition-colors ${notif.lida ? "hover:bg-slate-50" : "bg-blue-50/40 hover:bg-blue-50/70"}`}>

                    {/* Icon */}
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: `${cfg.color}18` }}>
                      <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-snug ${notif.lida ? "text-slate-600" : "font-semibold text-slate-800"}`}>
                        {notif.titulo}
                      </p>
                      {notif.mensagem && (
                        <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{notif.mensagem}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-slate-400">{timeAgo(notif.created_at)}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${cfg.color}15`, color: cfg.color }}>
                          {cfg.label}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {!notif.lida && (
                        <div className="w-2 h-2 rounded-full mt-1" style={{ backgroundColor: tc500 }} />
                      )}
                      <button onClick={(e) => deleteNotif(e, notif.id)}
                        className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-rose-400 transition-all p-0.5">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {hasMore && (
            <button onClick={() => fetchNotifs(page + 1, true)}
              className="w-full py-3 text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors">
              Carregar mais
            </button>
          )}
        </div>
      </div>
    </>
  );
}
